/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import {
  useState,
  type FunctionComponent,
  type JSX,
  type KeyboardEvent
} from "react";

import "./FilterStrip.scss";
import { FilterStripProps, FilterStripOption } from "./FilterStripProps";
import { DeviceModel } from "../../../model/device";

/**
 * FilterStrip — a generic, reusable row of filter buttons.
 *
 * Any screen can use this component to render a set of filter options.
 * The `options` array is intended to be populated from an API response so
 * that the available filters are dynamic; when no API data is available,
 * callers should pass a static default list
 *
 * Focus state (D-pad navigation) is managed externally — the parent screen's
 * Redux slice tracks `focusedIndex` and `isFocused` and passes them as props.
 */
const FilterStrip: FunctionComponent<FilterStripProps> = (
  props: FilterStripProps
): JSX.Element => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, sonarjs/no-unused-vars, sonarjs/no-dead-store -- Required by CodingGuidelines §7 (D-09) for logging context
  const TAG: string = "FilterStrip";
  const {
    options,
    selectedId,
    selectedIds,
    isMultiSelect = false,
    selectOnFocus = true,
    variant = "tabs",
    focusedIndex,
    isFocused,
    onSelect
  } = props;
  const isSmartTv: boolean = DeviceModel.isTvDevice();

  // TV pointer hover only — mirrors MenuStrip's `--ghost-hover` (see
  // MenuStrip.tsx): some Smart TV remotes drive an on-screen pointer
  // alongside the D-pad, and this is the dim highlight it leaves on a tab it
  // crosses without the D-pad ever landing there. Cleared on any arrow-key
  // press so an old pointer position can't leave a stale dim behind once the
  // D-pad takes over — same guard as MenuStrip's `handleKeyDown`.
  const [ghostHoveredIndex, setGhostHoveredIndex] = useState<number | null>(
    null
  );

  return (
    <div
      className={`filter-strip filter-strip--${variant}`}
      role={isMultiSelect ? "group" : "tablist"}
      aria-label={isMultiSelect ? "Genre filters" : "Content filters"}
    >
      {options.map((option: FilterStripOption, index: number): JSX.Element => {
        const isFilterSelected: boolean = isMultiSelect
          ? selectedIds !== undefined && selectedIds.includes(option.id)
          : option.id === selectedId;
        const isFilterFocused: boolean = isFocused && focusedIndex === index;

        // TV pointer hover on a tab the D-pad hasn't landed on — same dim
        // treatment as MenuStrip's `--ghost-hover` (opacity + gradient, see
        // FilterStrip.scss), not to be confused with `--ghost` below, which
        // is the unrelated "selected, strip unfocused" grey pill. Excluded
        // for the selected tab itself: that pill already carries the grey
        // "ghost" look below, and dimming it further on hover read as a
        // third, unintended state.
        const isFilterGhostHovered: boolean =
          isSmartTv &&
          ghostHoveredIndex === index &&
          !isFilterFocused &&
          !isFilterSelected;

        // On TV the selected tab's pill fades to a grey "ghost" once D-pad focus
        // leaves the strip for the content below — the filter is still yours,
        // but the strip is no longer where you are. Focused or in the browser
        // (pointer, no focus region), it keeps the full white pill.
        const isFilterGhosted: boolean =
          isSmartTv && isFilterSelected && !isFocused;

        // The selected tab carries its pill; the D-pad cursor adds a ring on top
        // (Figma: My Hub Detail "Tab" — nodes 2866:21246 TV / 2731:3139
        // browser). The two states are independent, so a selected tab under the
        // cursor shows both.
        const buttonClass = [
          "filter-strip__button",
          isFilterSelected && "filter-strip__button--selected",
          isMultiSelect &&
            isFilterSelected &&
            "filter-strip__button--multiple-selected",
          isFilterGhosted && "filter-strip__button--ghost",
          isFilterGhostHovered && "filter-strip__button--ghost-hover",
          isFilterFocused && "filter-strip__button--focused"
        ]
          .filter(Boolean)
          .join(" ");

        return (
          <button
            key={option.id}
            className={buttonClass}
            data-filter-index={index}
            type="button"
            role={isMultiSelect ? "checkbox" : "tab"}
            aria-checked={isMultiSelect ? isFilterSelected : undefined}
            aria-selected={isMultiSelect ? undefined : isFilterSelected}
            onClick={(): void => {
              onSelect(option.id, index);
            }}
            onFocus={(): void => {
              if (!isMultiSelect && selectOnFocus) {
                onSelect(option.id, index);
              }
            }}
            onKeyDown={
              isSmartTv
                ? (event: KeyboardEvent<HTMLButtonElement>): void => {
                    if (
                      event.key === "ArrowLeft" ||
                      event.key === "ArrowRight" ||
                      event.key === "ArrowUp" ||
                      event.key === "ArrowDown"
                    ) {
                      setGhostHoveredIndex(null);
                    }
                  }
                : undefined
            }
            onMouseEnter={
              isSmartTv ? (): void => setGhostHoveredIndex(index) : undefined
            }
            onMouseLeave={
              isSmartTv ? (): void => setGhostHoveredIndex(null) : undefined
            }
          >
            {option.label}
            {isMultiSelect && isFilterSelected && (
              <span className="filter-strip__checkmark" aria-hidden="true" />
            )}
          </button>
        );
      })}
    </div>
  );
};

export default FilterStrip;
